/*
 * useTreeSelection
 *
 * Purpose:
 * - Own ALL Vessel/Voyage/Route tree state at a level ABOVE the panel that
 *   renders the tree, so it survives the panel unmounting (e.g. switching
 *   between the Vessel and Voyage Planner panels) instead of resetting.
 *
 * What it owns:
 * - The tree-selection reducer (selection + expand/collapse state).
 * - The fetched tree data (vessels/voyages/routes via useRouteTreeData), so the
 *   tree rows stay populated after a panel remount instead of going blank.
 * - The drill-down navigation position (which level + drilled vessel/voyage),
 *   so the user stays where they navigated after a panel remount.
 *
 * Notes:
 * - Action creators/selectors continue to live in features/routeTree/state.
 * - Entities live in the RouteStore; useRouteTreeData loads into it directly.
 */

import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
  initialTreeSelectionState,
  treeSelectionActions,
  treeSelectionReducer,
} from "@/features/routeTree/state/VesselTreeState.jsx";
import useRouteTreeData from "@/features/routeTree/hooks/useRouteTreeData.jsx";
import usePruneRemovedRouteSelection from "@/features/routeTree/hooks/usePruneRemovedRouteSelection.jsx";

export const NO_VOYAGE_DRILL_ID = "no-voyage";

export default function useTreeSelection({
  accessContext,
  setAccessContext,
}) {
  const [treeState, treeDispatch] = useReducer(
    treeSelectionReducer,
    initialTreeSelectionState,
  );

  // Drill-down navigation position: which level is shown and which vessel/voyage
  // has been drilled into. Pure navigation state (separate from selection).
  const [drillPath, setDrillPath] = useState({
    level: "vessels", // "vessels" | "voyages" | "routes"
    vesselId: null,
    voyageId: null,
  });

  // External request to drill into a vessel (e.g. clicking the map marker).
  // RouteDrillDown consumes this and runs its drillIntoVessel flow. Shaped as
  // { vesselName, nonce, recenter, voyageId } so repeated clicks on the same
  // vessel re-trigger. voyageId (numeric id, NO_VOYAGE_DRILL_ID, or null) drills
  // one level deeper into that voyage's routes; null stops at the voyage list.
  const [drillRequest, setDrillRequest] = useState(null);
  const requestDrillToVessel = useCallback(
    (vesselName, { recenter = true, voyageId = null } = {}) => {
      if (!vesselName) return;
      setDrillRequest((prev) => ({
        vesselName,
        recenter,
        voyageId,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
    },
    [],
  );
  // Clear the pending request once it's been acted on / deselected. Without
  // this the stale request lingers, and since RouteDrillDown's
  // already-handled guard is a ref that dies when the panel unmounts, reopening
  // the panel would re-consume the old request and re-select the vessel.
  const clearDrillRequest = useCallback(() => setDrillRequest(null), []);

  // A drill request for a vessel other than the marked one is stale and
  // would block the follow-marked-vessel drill.
  const markedVesselName = treeState.ui.markedVessel?.vesselName ?? "";
  useEffect(() => {
    setDrillRequest((prev) =>
      prev?.vesselName && prev.vesselName !== markedVesselName ? null : prev,
    );
  }, [markedVesselName]);

  // Compat views of the reducer-owned marked vessel: the many consumers keep
  // reading a list; the setter dispatches the atomic intent actions.
  const markedVesselNames = useMemo(
    () => (treeState.ui.markedVessel ? [treeState.ui.markedVessel] : []),
    [treeState.ui.markedVessel],
  );
  const setMarkedVesselNames = useCallback((list) => {
    const entry = Array.isArray(list) ? list[0] : list;
    if (entry?.vesselName) {
      treeDispatch(
        treeSelectionActions.vesselMarked(
          entry.vesselName,
          entry.vesselId ?? null,
        ),
      );
    } else {
      treeDispatch(treeSelectionActions.vesselUnmarked());
    }
  }, []);

  const treeData = useRouteTreeData({
    accessContext,
    setAccessContext,
  });

  usePruneRemovedRouteSelection({
    selectedRouteIds: treeState.ui.selectedRouteIds,
    treeDispatch,
  });

  return {
    treeState,
    treeDispatch,
    drillPath,
    setDrillPath,
    drillRequest,
    requestDrillToVessel,
    clearDrillRequest,
    treeData,
    markedVesselNames,
    setMarkedVesselNames,
  };
}
